import { borderCss, decorationSvg, svgDataUrl } from '../designs/decorations';
import { fontStack, getFont } from '../designs/fonts';
import type { Look } from '../designs/look';
import { drawStrokes } from '../drawing/render';
import { visibleLines } from '../notes/model';
import { getAsset } from '../storage/repo';
import type { Note, NoteImage } from '../storage/schemas';
import { getDevice, getWallpaper, paintWallpaper } from './wallpapers';

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

async function loadAssetImage(id: string): Promise<HTMLImageElement | null> {
  const a = await getAsset(id);
  if (!a) return null;
  const url = URL.createObjectURL(new Blob([a.data], { type: a.mime }));
  try {
    return await loadImage(url);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawPattern(ctx: CanvasRenderingContext2D, look: Look, W: number, H: number) {
  const pitch = W * 0.05 * look.patternScale;
  const lw = Math.max(1, W / 380);
  ctx.fillStyle = look.line;
  if (look.pattern === 'grid') {
    for (let x = 0; x < W; x += pitch) ctx.fillRect(Math.round(x), 0, lw, H);
    for (let y = 0; y < H; y += pitch) ctx.fillRect(0, Math.round(y), W, lw);
  } else if (look.pattern === 'ruled') {
    for (let y = pitch - lw; y < H; y += pitch) ctx.fillRect(0, Math.round(y), W, lw);
  } else if (look.pattern === 'dots') {
    const r = Math.max(0.9, W / 420);
    for (let y = pitch / 2; y < H; y += pitch)
      for (let x = pitch / 2; x < W; x += pitch) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
  }
}

async function drawImages(ctx: CanvasRenderingContext2D, images: NoteImage[], W: number, H: number) {
  for (const img of [...images].sort((a, b) => a.z - b.z)) {
    const el = await loadAssetImage(img.assetId).catch(() => null);
    if (!el) continue;
    const w = img.w * W;
    const h = img.w * img.aspect * W;
    ctx.save();
    ctx.translate(img.x * W, img.y * H);
    ctx.rotate((img.rotation * Math.PI) / 180);
    const c = img.crop;
    const sx = c.l * el.naturalWidth;
    const sy = c.t * el.naturalHeight;
    const sw = el.naturalWidth * (1 - c.l - c.r);
    const sh = el.naturalHeight * (1 - c.t - c.b);
    ctx.drawImage(el, sx, sy, sw, sh, -w / 2 + c.l * w, -h / 2 + c.t * h, w * (1 - c.l - c.r), h * (1 - c.t - c.b));
    ctx.restore();
  }
}

// Same paths as the on-screen CheckboxGlyph. Created lazily (Path2D is not available outside browsers).
const CHECK_BOX_D = 'M6.2 7.4 C13 6.1 22.6 5.6 33.4 6.6 C34.6 14.8 34.9 24.1 33.9 33.6 C24.7 34.5 15.3 34.7 6.6 34.1 C5.6 25.7 5.4 16.2 6.2 7.4 Z';
const CHECK_X_D = 'M10.5 10.8 C17 17.2 23.6 24.1 29.8 30.2 M29.4 10.2 C22.9 16.9 16.6 23.6 10.6 30.4';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  if (!text) return [''];
  const out: string[] = [];
  // Split on spaces, but allow breaking inside long words / CJK.
  const tokens = text.match(/\S+\s*|\s+/g) ?? [text];
  let line = '';
  for (const tok of tokens) {
    const test = line + tok;
    if (ctx.measureText(test.trimEnd()).width <= maxW || !line) {
      if (ctx.measureText(test.trimEnd()).width > maxW) {
        // Hard-break an over-long token.
        let chunk = '';
        for (const ch of [...test]) {
          if (ctx.measureText(chunk + ch).width > maxW && chunk) {
            out.push(chunk);
            chunk = ch;
          } else chunk += ch;
        }
        line = chunk;
      } else line = test;
    } else {
      out.push(line.trimEnd());
      line = tok.trimStart();
    }
  }
  out.push(line.trimEnd());
  return out;
}

async function drawText(ctx: CanvasRenderingContext2D, note: Note, look: Look, W: number, H: number) {
  const font = getFont(look.font);
  const stack = fontStack(look.font);
  const base = (note.style.fontScale * font.scale * W) / 100;
  const lh = note.style.lineHeight;
  const left = W * 0.05 + W * 0.06;
  const right = W - W * 0.05 - W * 0.045;
  const top = W * 0.05;
  const bottom = H - W * 0.05;
  const lines = visibleLines(note, false);
  try {
    await document.fonts.load(`${base}px ${stack}`, lines.map((l) => l.text).join(' ') || 'Aa');
  } catch {
    /* fall back to whatever is available */
  }
  type Row = { text: string; size: number; check: boolean; first: boolean; checked: boolean; marks: Note['lines'][number]['marks'] };
  const rows: Row[] = [];
  for (const l of lines) {
    const m = l.marks ?? {};
    const size = m.heading ? base * 1.28 : base;
    ctx.font = `${m.italic ? 'italic ' : ''}${m.bold ? '700 ' : ''}${size}px ${stack}`;
    const indent = l.kind === 'check' ? size * (0.8 + 0.42) : 0;
    const wrapped = wrap(ctx, l.text, right - left - indent);
    wrapped.forEach((t, i) => rows.push({ text: t, size, check: l.kind === 'check', first: i === 0, checked: l.checked, marks: l.marks }));
  }
  const total = rows.reduce((s, r) => s + r.size * lh, 0);
  let y = note.style.vAlign === 'top' ? top : Math.max(top, top + (bottom - top - total) / 2);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, top, W, bottom - top);
  ctx.clip();
  for (const r of rows) {
    const m = r.marks ?? {};
    const lineH = r.size * lh;
    ctx.font = `${m.italic ? 'italic ' : ''}${m.bold ? '700 ' : ''}${r.size}px ${stack}`;
    ctx.fillStyle = look.ink;
    ctx.textBaseline = 'middle';
    const cy = y + lineH / 2;
    const indent = r.check ? r.size * (0.8 + 0.42) : 0;
    const textW = ctx.measureText(r.text).width;
    let x = left + indent;
    if (note.style.align === 'center') x = left + (right - left - textW - indent) / 2 + indent;
    if (note.style.align === 'right') x = right - textW;
    if (r.check && r.first) {
      const s = r.size * 0.8;
      ctx.save();
      ctx.translate(x - indent, cy - s / 2);
      ctx.scale(s / 40, s / 40);
      ctx.strokeStyle = look.ink;
      ctx.lineWidth = 3.4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(new Path2D(CHECK_BOX_D));
      if (r.checked) {
        ctx.lineWidth = 3.6;
        ctx.stroke(new Path2D(CHECK_X_D));
      }
      ctx.restore();
    }
    ctx.fillText(r.text, x, cy);
    const thick = Math.max(1, r.size * 0.08);
    if (r.check && r.checked && note.style.completed !== 'hide') ctx.fillRect(x, cy - thick / 2, textW, thick);
    if (m.underline) ctx.fillRect(x, cy + r.size * 0.42, textW, thick * 0.9);
    y += lineH;
  }
  ctx.restore();
}

/** Render a note to a canvas `width` px wide, matching the on-screen card. */
export async function renderNoteCanvas(note: Note, look: Look, width = 1170): Promise<HTMLCanvasElement> {
  const aspect = note.style.compact ? 4.4 : 2.2;
  const W = Math.round(width);
  const H = Math.round(W / aspect);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  roundRect(ctx, 0, 0, W, H, W / 16);
  ctx.save();
  ctx.clip();
  ctx.fillStyle = look.paper;
  ctx.fillRect(0, 0, W, H);
  drawPattern(ctx, look, W, H);
  const deco = decorationSvg(look.decoration, look.accent, look.line, aspect);
  if (deco) {
    const img = await loadImage(svgDataUrl(deco)).catch(() => null);
    if (img) ctx.drawImage(img, 0, 0, W, H);
  }
  const border = look.border && borderCss(look.border) ? look.border : null;
  if (border) {
    ctx.save();
    const bw = border.width * W;
    ctx.strokeStyle = border.color;
    ctx.lineWidth = bw;
    if (border.style === 'dashed' || border.style === 'stitch') ctx.setLineDash([bw * 3, bw * 2]);
    const inset = border.style === 'stitch' ? W * 0.022 : bw / 2;
    roundRect(ctx, inset, inset, W - inset * 2, H - inset * 2, Math.max(1, W / 16 - inset));
    ctx.stroke();
    if (border.style === 'double') {
      ctx.lineWidth = bw / 3;
      roundRect(ctx, bw * 1.6, bw * 1.6, W - bw * 3.2, H - bw * 3.2, W / 16 - bw * 1.6);
      ctx.stroke();
    }
    ctx.restore();
  }
  await drawImages(ctx, note.images.filter((i) => !i.aboveText), W, H);
  await drawText(ctx, note, look, W, H);
  await drawImages(ctx, note.images.filter((i) => i.aboveText), W, H);
  if (note.strokes.length) {
    const layer = document.createElement('canvas');
    layer.width = W;
    layer.height = H;
    drawStrokes(layer.getContext('2d')!, note.strokes, W);
    ctx.drawImage(layer, 0, 0);
  }
  if (note.style.watermark) {
    ctx.save();
    const fs = W * 0.0215;
    try {
      await document.fonts.load(`${fs}px Mynerve`, 'MemoNest');
    } catch {
      /* ignore */
    }
    ctx.translate(W - W * 0.04 - W * 0.084, H - W * 0.032 - fs * 2.4);
    ctx.rotate((-9 * Math.PI) / 180);
    ctx.fillStyle = look.accent;
    ctx.font = `${fs}px Mynerve, Caveat, cursive`;
    ctx.textBaseline = 'top';
    ctx.fillText('Memo', 0, 0);
    ctx.fillText('Nest', W * 0.016, fs * 0.92);
    ctx.fillRect(0, fs * 2, W * 0.07, W * 0.0045);
    ctx.restore();
  }
  ctx.restore();
  return canvas;
}

export interface LockScreenOptions {
  wallpaper: string;
  customWallpaper?: HTMLImageElement | null;
  device: string;
  position: number;
  scale: number;
  contrast: boolean;
  showClock: boolean;
}

/** Full-resolution lock-screen wallpaper with the note composited where the preview shows it. */
export async function renderLockScreenImage(note: Note, look: Look, o: LockScreenOptions): Promise<HTMLCanvasElement> {
  const d = getDevice(o.device);
  const W = Math.round(d.w * d.dpr);
  const H = Math.round(d.h * d.dpr);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  if (o.customWallpaper) {
    const img = o.customWallpaper;
    const s = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    ctx.drawImage(img, (W - img.naturalWidth * s) / 2, (H - img.naturalHeight * s) / 2, img.naturalWidth * s, img.naturalHeight * s);
  } else paintWallpaper(ctx, getWallpaper(o.wallpaper), W, H);
  const cardW = W * 0.92 * o.scale;
  const card = await renderNoteCanvas(note, look, cardW);
  const x = (W - cardW) / 2;
  const y = (H - card.height) * o.position;
  if (o.contrast) {
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.fillRect(0, 0, W, H);
  }
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.18)';
  ctx.shadowBlur = W * 0.03;
  ctx.shadowOffsetY = W * 0.006;
  ctx.drawImage(card, x, y);
  ctx.restore();
  return canvas;
}

export function canvasToBlob(c: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('Export failed'))), type));
}

export async function shareOrDownload(blob: Blob, filename: string): Promise<'shared' | 'downloaded'> {
  const file = new File([blob], filename, { type: blob.type });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.canShare?.({ files: [file] }) && /Mobi|Android|iPhone|iPad/.test(navigator.userAgent)) {
    try {
      await navigator.share({ files: [file], title: filename });
      return 'shared';
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'shared';
    }
  }
  const { downloadBlob } = await import('../storage/backup');
  downloadBlob(blob, filename);
  return 'downloaded';
}
